import React, { useState, useCallback } from 'react';
import './Submit.css';
import DeadlineCountdown from './DeadlineCountdown';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Helper function to count Chinese equivalent characters
// 1 English word = 2 Chinese characters
const countChineseEquivalent = (text) => {
  if (!text) return 0;
  
  // Count Chinese characters
  const chineseChars = (text.match(/[\u4e00-\u9fa5]/g) || []).length;
  
  // Count English words (sequences of letters)
  const englishWords = (text.match(/[a-zA-Z]+/g) || []).length;
  
  // Count numbers as 1 character each
  const numbers = (text.match(/\d/g) || []).length;
  
  // English words count as 2 Chinese characters each
  return chineseChars + (englishWords * 2) + numbers;
};

// Helper function to validate keyword length (4 Chinese chars OR 2 English words)
const validateKeywordLength = (text) => {
  if (!text) return { valid: true, count: 0, max: 4, isEnglish: false };
  
  const chineseChars = (text.match(/[\u4e00-\u9fa5]/g) || []).length;
  const englishWords = (text.match(/[a-zA-Z]+/g) || []).length;
  
  // If has English words and no Chinese, treat as English
  const isEnglish = englishWords > 0 && chineseChars === 0;
  
  if (isEnglish) {
    return { valid: englishWords <= 2, count: englishWords, max: 2, isEnglish: true };
  } else {
    // Mixed or Chinese: count Chinese chars only
    return { valid: chineseChars <= 4, count: chineseChars, max: 4, isEnglish: false };
  }
};

// Helper function to validate keyword
const validateKeyword = (text) => {
  const keywordLength = validateKeywordLength(text);
  if (!keywordLength.valid) {
    return { valid: false, error: '关键词长度超出限制' };
  }
  return { valid: true };
};

// Original countChineseEquivalent function
const countChineseEquivalentOriginal = (text) => {
  if (!text) return 0;
  
  // Count Chinese characters
  const chineseChars = (text.match(/[\u4e00-\u9fa5]/g) || []).length;
  
  // Count English words (sequences of letters)
  const englishWords = (text.match(/[a-zA-Z]+/g) || []).length;
  
  // Count numbers as 1 character each
  const numbers = (text.match(/\d/g) || []).length;
  
  // English words count as 2 Chinese characters each
  return chineseChars + (englishWords * 2) + numbers;
};

// Helper function to validate bio (150 chars for Chinese OR 100 words for English)
const validateBioLength = (text) => {
  if (!text) return { valid: true, charCount: 0, wordCount: 0, isEnglish: false };
  
  const charCount = text.length;
  const chineseChars = (text.match(/[\u4e00-\u9fa5]/g) || []).length;
  const wordCount = (text.match(/[a-zA-Z]+/g) || []).length;
  
  // If has English words and no/few Chinese chars, treat as English (100 words limit)
  // Otherwise treat as Chinese (150 chars limit)
  const isEnglish = wordCount > 0 && chineseChars < wordCount;
  
  let valid;
  if (isEnglish) {
    valid = wordCount <= 100;
  } else {
    valid = charCount <= 150;
  }
  
  return { valid, charCount, wordCount, isEnglish };
};

// Helper function to count title (40 Chinese chars OR 20 English words)
const countTitleLength = (text) => {
  if (!text) return { count: 0, max: 40, isEnglish: false };
  
  // Check if primarily English
  const chineseChars = (text.match(/[\u4e00-\u9fa5]/g) || []).length;
  const englishWords = (text.match(/[a-zA-Z]+/g) || []).length;
  
  // If more English words than Chinese chars, treat as English
  const isEnglish = englishWords > chineseChars;
  
  if (isEnglish) {
    return { count: englishWords, max: 20, isEnglish: true };
  } else {
    // Mixed or Chinese: count as Chinese equivalent
    return { count: countChineseEquivalent(text), max: 40, isEnglish: false };
  }
};

function Submit({ language, previewMode = false, countdownTarget }) {
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isSubmissionOpen, setIsSubmissionOpen] = useState(previewMode);
  
  // Form state
  const [formData, setFormData] = useState({
    forum: '',
    title: '',
    abstract: '',
    socialScienceEnabled: false,
    socialScienceContent: '',
    keywords: [],
    keywordInput: '',
    authorName: '',
    authorAffiliation: '',
    authorSeniority: '',
    authorEmail: '',
    authorPhone: '',
    authorWechat: '',
    authorCountry: '',
    authorCountryOther: '',
    authorBio: '',
    originalityAgreed: false
  });

  const texts = {
    zh: {
      pageTitle: '2026博雅国际电子游戏学术年会在线投稿',
      pageDescription: '请在截止日期前完成以下信息并上传所需材料。支持中文/英文投稿。相关信息已做加密处理，请放心填写。',
      
      // Forum
      forumLabel: '投稿分论坛',
      forumPlaceholder: '请选择分论坛',
      forumOptions: ['赛博文本', '游戏感', '帝国的游戏', '游戏改变世界'],
      forumRequired: '请选择投稿分论坛',
      
      // Title
      titleLabel: '题目',
      titlePlaceholder: '请输入论文题目',
      titleHint: '中文不超过40字；英文不超过20个单词',
      titleRemaining: '剩余',
      titleChars: '字',
      titleWords: '词',
      titleError: '题目超出字数上限',
      titleRequired: '请输入论文题目',
      
      // Abstract
      abstractLabel: '摘要',
      abstractPlaceholder: '请输入论文摘要',
      abstractHint: '不超过800汉字（1英文单词=2汉字）',
      abstractCount: '当前',
      abstractUnit: '汉字等值',
      abstractError: '摘要超出800个汉字等值上限（英文单词按两个汉字计）',
      abstractRequired: '请输入论文摘要',
      
      // Social Science
      socialScienceLabel: '社科类论文补充情况介绍',
      socialScienceCheckbox: '本论文为社科类论文，需要补充说明',
      socialSciencePlaceholder: '请填写社科类论文补充内容',
      socialScienceHint: '不超过1000汉字（1英文单词=2汉字）',
      socialScienceError: '补充内容超出1000个汉字等值上限（英文单词按两个汉字计）',
      socialScienceRequired: '请填写社科类补充内容',
      
      // Keywords
      keywordsLabel: '关键词',
      keywordsPlaceholder: '输入关键词后按回车添加',
      keywordsAdd: '添加',
      keywordsHint: '1-5个关键词，每个关键词限4个汉字或2个英文单词',
      keywordsRemaining: '还可添加',
      keywordsUnit: '个',
      keywordsClickToRemove: '点击删除',
      keywordsMinError: '请至少添加1个关键词',
      keywordsMaxError: '最多只能添加5个关键词',
      keywordsLengthError: '关键词超出长度限制（4个汉字或2个英文单词）',
      
      // Author Info
      authorInfoTitle: '作者信息',
      authorNameLabel: '姓名',
      authorNamePlaceholder: '请输入姓名',
      authorNameRequired: '请输入作者姓名',
      authorNameNote: '多位作者仅填写第一作者信息',
      
      authorAffiliationLabel: '单位',
      authorAffiliationPlaceholder: '请输入单位（若无请填写"无"）',
      authorAffiliationRequired: '请输入作者单位',
      
      authorSeniorityLabel: '年资',
      authorSeniorityPlaceholder: '请选择年资',
      authorSeniorityOptions: ['高中生', '本科生', '硕士生', '博士生', '博士及以上', '独立学者'],
      authorSeniorityRequired: '请选择年资',
      
      authorEmailLabel: '邮箱',
      authorEmailPlaceholder: '请输入邮箱地址',
      authorEmailError: '请输入有效的邮箱地址',
      authorEmailRequired: '请输入邮箱地址',
      
      authorPhoneLabel: '联系电话',
      authorPhonePlaceholder: '请输入联系电话',
      authorPhoneError: '请输入有效的联系电话',
      
      authorWechatLabel: '微信号',
      authorWechatPlaceholder: '请输入微信号（可选）',
      
      authorBioLabel: '个人简介',
      authorBioPlaceholder: '请输入个人简介（可选）',
      authorBioHint: '不超过150字符或100词',
      authorBioError: '个人简介超出限制（150字符或100词）',
      
      authorCountryLabel: '国家/地区',
      authorCountryPlaceholder: '请选择国家/地区',
      authorCountryRequired: '请选择国家/地区',
      authorCountryOtherPlaceholder: '请输入国家/地区名称',
      authorCountryOtherRequired: '请输入国家/地区名称',
      
      // Originality
      originalityLabel: '原创授权声明',
      originalityText: '我确认：所投稿件须为原创，禁止抄袭，文责自负，请勿一稿多投。请确定投递的论文或摘要未在任何渠道公开出版或发表。论文、摘要需在总体上符合建议选题方向，论证充分，具备理论或实用价值，有一定创新性。投稿的文章以同行匿名评审的方式进行评估，评估内容主要包括原创性、研究深度、会议主题相关性、学术价值、可读性等。如使用 AI 工具辅助写作，务必在稿件中明确声明使用的 AI工具名称及具体用途。如果使用 AI工具直接生成大段内容，请务必附上完整的提示词。审稿流程包含学术道德审查，但会将不恰当的AI使用主要视为质量问题而非道德问题。',
      originalityRequired: '请勾选原创授权声明',
      
      // Buttons
      resetButton: '重置',
      submitButton: '提交',
      
      // Success
      successTitle: '投稿成功',
      successMessage: '您的稿件已成功提交，我们将尽快审核并与您联系。',
      backButton: '返回',
      
      // Errors
      errorTitle: '请修正以下错误：',
      
      // Call for Papers
      cfpTitle: '征稿文件',
      cfpTheme: '演绎/延异规则：创造性地思考电子游戏中的"力"',
      cfpDescription: '请下载并阅读征稿文件以了解详细投稿要求。',
      cfpDownload: '下载征稿文件 (PDF)',
      contactInfo: '有任何疑问请联系pkugarc@163.com',
      authorFieldMaxError: '字段内容不能超过40个字符',
      
      // Submission closed
      submissionClosedTitle: '投稿已关闭',
      submissionClosedMessage: '感谢您的关注！投稿通道已于2026年2月28日23:59（北京时间）关闭。您仍可查看下方的征稿文件。'
    },
    en: {
      pageTitle: '2026 Boya International Video Game Academic Conference Online Submission',
      pageDescription: 'Please complete the following information before the deadline. Chinese/English submissions are supported. All information is encrypted for your security.',
      
      // Forum
      forumLabel: 'Sub-forum',
      forumPlaceholder: 'Select a sub-forum',
      forumOptions: ['Cybertext', 'Playing With Feelings', 'Games of Empire', 'Reality is Broken'],
      forumRequired: 'Please select a sub-forum',
      
      // Title
      titleLabel: 'Title',
      titlePlaceholder: 'Enter paper title',
      titleHint: 'Max 40 Chinese characters or 20 English words',
      titleRemaining: 'Remaining',
      titleChars: 'chars',
      titleWords: 'words',
      titleError: 'Title exceeds the maximum length',
      titleRequired: 'Please enter the paper title',
      
      // Abstract
      abstractLabel: 'Abstract',
      abstractPlaceholder: 'Enter paper abstract',
      abstractHint: 'Max 800 Chinese character equivalents (1 English word = 2 Chinese chars)',
      abstractCount: 'Current',
      abstractUnit: 'Chinese char equivalents',
      abstractError: 'Abstract exceeds 800 Chinese character equivalents (1 English word = 2 Chinese chars)',
      abstractRequired: 'Please enter the paper abstract',
      
      // Social Science
      socialScienceLabel: 'Social Science Paper Supplement',
      socialScienceCheckbox: 'This is a social science paper requiring additional information',
      socialSciencePlaceholder: 'Enter supplementary information for social science paper',
      socialScienceHint: 'Max 1000 Chinese character equivalents (1 English word = 2 Chinese chars)',
      socialScienceError: 'Supplement exceeds 1000 Chinese character equivalents (1 English word = 2 Chinese chars)',
      socialScienceRequired: 'Please fill in the social science supplement',
      
      // Keywords
      keywordsLabel: 'Keywords',
      keywordsPlaceholder: 'Enter keyword and press Enter to add',
      keywordsAdd: 'Add',
      keywordsHint: '1-5 keywords, max 4 Chinese chars or 2 English words each',
      keywordsRemaining: 'Can add',
      keywordsUnit: 'more',
      keywordsClickToRemove: 'Click to remove',
      keywordsMinError: 'Please add at least 1 keyword',
      keywordsMaxError: 'Maximum 5 keywords allowed',
      keywordsLengthError: 'Keyword exceeds length limit (4 Chinese chars or 2 English words)',
      
      // Author Info
      authorInfoTitle: 'Author Information',
      authorNameLabel: 'Name',
      authorNamePlaceholder: 'Enter your name',
      authorNameRequired: 'Please enter author name',
      authorNameNote: 'For multiple authors, only fill in the first author\'s information',
      
      authorAffiliationLabel: 'Affiliation',
      authorAffiliationPlaceholder: 'Enter affiliation (enter "None" if not applicable)',
      authorAffiliationRequired: 'Please enter author affiliation',
      
      authorSeniorityLabel: 'Academic Level',
      authorSeniorityPlaceholder: 'Select academic level',
      authorSeniorityOptions: ['High School Student', 'Undergraduate', 'Master\'s Student', 'PhD Student', 'PhD or Above', 'Independent Scholar'],
      authorSeniorityRequired: 'Please select academic level',
      
      authorEmailLabel: 'Email',
      authorEmailPlaceholder: 'Enter email address',
      authorEmailError: 'Please enter a valid email address',
      authorEmailRequired: 'Please enter email address',
      
      authorPhoneLabel: 'Phone',
      authorPhonePlaceholder: 'Enter phone number',
      authorPhoneError: 'Please enter a valid phone number',
      
      authorWechatLabel: 'WeChat ID',
      authorWechatPlaceholder: 'Enter WeChat ID (optional)',
      
      authorBioLabel: 'Personal Bio',
      authorBioPlaceholder: 'Enter personal bio (optional)',
      authorBioHint: 'Max 150 characters or 100 words',
      authorBioError: 'Personal bio exceeds limit (150 characters or 100 words)',
      
      authorCountryLabel: 'Country/Region',
      authorCountryPlaceholder: 'Select country/region',
      authorCountryRequired: 'Please select country/region',
      authorCountryOtherPlaceholder: 'Enter country/region name',
      authorCountryOtherRequired: 'Please enter country/region name',
      
      // Originality
      originalityLabel: 'Originality Declaration',
      originalityText: 'I confirm: All submissions must be original work. Plagiarism is prohibited, and authors bear full responsibility for their content. Please do not submit the same work to multiple venues. Please ensure that the submitted paper or abstract has not been publicly published or presented through any channel. Papers and abstracts should generally align with the suggested topics, be well-argued, possess theoretical or practical value, and demonstrate a degree of innovation. Submissions will be evaluated through anonymous peer review, focusing on originality, research depth, relevance to conference themes, academic value, and readability. If AI tools are used to assist in writing, please clearly state the name of the AI tool and its specific purpose in the manuscript. If AI tools are used to directly generate substantial content, please include the complete prompts. The review process includes academic ethics review, but inappropriate AI use will primarily be treated as a quality issue rather than an ethical issue.',
      originalityRequired: 'Please agree to the originality declaration',
      
      // Buttons
      resetButton: 'Reset',
      submitButton: 'Submit',
      
      // Success
      successTitle: 'Submission Successful',
      successMessage: 'Your paper has been successfully submitted. We will review it and contact you soon.',
      backButton: 'Back',
      
      // Errors
      errorTitle: 'Please correct the following errors:',
      
      // Call for Papers
      cfpTitle: 'Call for Papers',
      cfpTheme: 'Transcribe/Transfigure the Rule: Perspectives on Videogames through the Lens of "Force"',
      cfpDescription: 'Please download and read the Call for Papers document for detailed submission requirements.',
      cfpDownload: 'Download Call for Papers (PDF)',
      contactInfo: 'For any questions, please contact pkugarc@163.com',
      authorFieldMaxError: 'Field content cannot exceed 40 characters',
      
      // Submission closed
      submissionClosedTitle: 'Submissions Closed',
      submissionClosedMessage: 'Thank you for your interest! The submission portal closed on February 28, 2026 at 23:59 (Beijing Time). You may still view the Call for Papers below.'
    }
  };

  // Handler for deadline status changes
  const handleDeadlineStatusChange = useCallback((isOpen) => {
    setIsSubmissionOpen(previewMode ? true : isOpen);
  }, [previewMode]);

  const countries = [
    { zh: '中国大陆', en: 'Mainland China' },
    { zh: '中国香港', en: 'Hong Kong, China' },
    { zh: '中国澳门', en: 'Macau, China' },
    { zh: '中国台湾', en: 'Taiwan, China' },
    { zh: '美国', en: 'United States' },
    { zh: '英国', en: 'United Kingdom' },
    { zh: '日本', en: 'Japan' },
    { zh: '韩国', en: 'South Korea' },
    { zh: '德国', en: 'Germany' },
    { zh: '法国', en: 'France' },
    { zh: '加拿大', en: 'Canada' },
    { zh: '澳大利亚', en: 'Australia' },
    { zh: '新加坡', en: 'Singapore' },
    { zh: '马来西亚', en: 'Malaysia' },
    { zh: '泰国', en: 'Thailand' },
    { zh: '印度尼西亚', en: 'Indonesia' },
    { zh: '越南', en: 'Vietnam' },
    { zh: '菲律宾', en: 'Philippines' },
    { zh: '印度', en: 'India' },
    { zh: '俄罗斯', en: 'Russia' },
    { zh: '意大利', en: 'Italy' },
    { zh: '西班牙', en: 'Spain' },
    { zh: '荷兰', en: 'Netherlands' },
    { zh: '比利时', en: 'Belgium' },
    { zh: '瑞士', en: 'Switzerland' },
    { zh: '瑞典', en: 'Sweden' },
    { zh: '挪威', en: 'Norway' },
    { zh: '丹麦', en: 'Denmark' },
    { zh: '芬兰', en: 'Finland' },
    { zh: '奥地利', en: 'Austria' },
    { zh: '波兰', en: 'Poland' },
    { zh: '巴西', en: 'Brazil' },
    { zh: '墨西哥', en: 'Mexico' },
    { zh: '阿根廷', en: 'Argentina' },
    { zh: '智利', en: 'Chile' },
    { zh: '新西兰', en: 'New Zealand' },
    { zh: '南非', en: 'South Africa' },
    { zh: '埃及', en: 'Egypt' },
    { zh: '以色列', en: 'Israel' },
    { zh: '土耳其', en: 'Turkey' },
    { zh: '沙特阿拉伯', en: 'Saudi Arabia' },
    { zh: '阿联酋', en: 'United Arab Emirates' },
    { zh: '其他', en: 'Other', isOther: true }
  ];

  const currentTexts = texts[language] || texts.en;

  const handleInputChange = useCallback((field, value) => {
    // For keyword input, enforce length limit in real-time
    if (field === 'keywordInput') {
      const validation = validateKeywordLength(value);
      if (!validation.valid) {
        return; // Don't allow input that exceeds limit
      }
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const addKeyword = useCallback(() => {
    const keyword = formData.keywordInput.trim();
    if (keyword && formData.keywords.length < 5 && !formData.keywords.includes(keyword)) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keyword],
        keywordInput: ''
      }));
    }
  }, [formData.keywordInput, formData.keywords]);

  const removeKeyword = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter((_, i) => i !== index)
    }));
  }, []);

  const handleKeywordKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  }, [addKeyword]);

  const validateForm = useCallback(() => {
    const newErrors = [];
    const t = currentTexts;

    // Forum validation
    if (!formData.forum) {
      newErrors.push(t.forumRequired);
    }

    // Title validation
    if (!formData.title.trim()) {
      newErrors.push(t.titleRequired);
    } else {
      const titleInfo = countTitleLength(formData.title);
      if (titleInfo.count > titleInfo.max) {
        newErrors.push(t.titleError);
      }
    }

    // Abstract validation
    if (!formData.abstract.trim()) {
      newErrors.push(t.abstractRequired);
    } else {
      const abstractCount = countChineseEquivalent(formData.abstract);
      if (abstractCount > 800) {
        newErrors.push(t.abstractError);
      }
    }

    // Social science validation
    if (formData.socialScienceEnabled) {
      if (!formData.socialScienceContent.trim()) {
        newErrors.push(t.socialScienceRequired);
      } else {
        const socialCount = countChineseEquivalent(formData.socialScienceContent);
        if (socialCount > 1000) {
          newErrors.push(t.socialScienceError);
        }
      }
    }

    // Keywords validation
    if (formData.keywords.length < 1) {
      newErrors.push(t.keywordsMinError);
    } else if (formData.keywords.length > 5) {
      newErrors.push(t.keywordsMaxError);
    }

    // Author name validation
    if (!formData.authorName.trim()) {
      newErrors.push(t.authorNameRequired);
    }

    // Author affiliation validation
    if (!formData.authorAffiliation.trim()) {
      newErrors.push(t.authorAffiliationRequired);
    }

    // Author seniority validation
    if (!formData.authorSeniority) {
      newErrors.push(t.authorSeniorityRequired);
    }

    // Email validation
    if (!formData.authorEmail.trim()) {
      newErrors.push(t.authorEmailRequired);
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.authorEmail)) {
        newErrors.push(t.authorEmailError);
      }
    }

    // Phone validation (loose)
    if (formData.authorPhone.trim()) {
      const phoneRegex = /^[\d\s\-+()]{6,20}$/;
      if (!phoneRegex.test(formData.authorPhone)) {
        newErrors.push(t.authorPhoneError);
      }
    }

    // Country validation
    if (!formData.authorCountry) {
      newErrors.push(t.authorCountryRequired);
    } else if ((formData.authorCountry === '其他' || formData.authorCountry === 'Other') && !formData.authorCountryOther.trim()) {
      newErrors.push(t.authorCountryOtherRequired);
    }

    // Bio validation (optional but has length limit)
    if (formData.authorBio.trim()) {
      const bioValidation = validateBioLength(formData.authorBio);
      if (!bioValidation.valid) {
        newErrors.push(t.authorBioError);
      }
    }

    // Originality validation
    if (!formData.originalityAgreed) {
      newErrors.push(t.originalityRequired);
    }

    return newErrors;
  }, [formData, currentTexts]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      // Scroll to error section
      document.querySelector('.submit-error-section')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      const response = await fetch(`${API_BASE}/api/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          forum: formData.forum,
          title: formData.title,
          abstract: formData.abstract,
          socialScienceEnabled: formData.socialScienceEnabled,
          socialScienceContent: formData.socialScienceContent,
          keywords: formData.keywords,
          authorName: formData.authorName,
          authorAffiliation: formData.authorAffiliation,
          authorSeniority: formData.authorSeniority,
          authorEmail: formData.authorEmail,
          authorPhone: formData.authorPhone,
          authorWechat: formData.authorWechat,
          authorCountry: formData.authorCountry,
          authorCountryOther: formData.authorCountryOther,
          authorBio: formData.authorBio
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitted(true);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setErrors([data.error || (language === 'zh' ? '提交失败，请稍后重试' : 'Submission failed, please try again')]);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setErrors([language === 'zh' ? '网络错误，请检查网络连接后重试' : 'Network error, please check your connection and try again']);
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, formData, language]);

  const handleReset = useCallback(() => {
    setFormData({
      forum: '',
      title: '',
      abstract: '',
      socialScienceEnabled: false,
      socialScienceContent: '',
      keywords: [],
      keywordInput: '',
      authorName: '',
      authorAffiliation: '',
      authorSeniority: '',
      authorEmail: '',
      authorPhone: '',
      authorWechat: '',
      authorCountry: '',
      authorCountryOther: '',
      authorBio: '',
      originalityAgreed: false
    });
    setErrors([]);
  }, []);

  const handleBack = useCallback(() => {
    setSubmitted(false);
    handleReset();
  }, [handleReset]);

  // Calculate counts
  const titleInfo = countTitleLength(formData.title);
  const abstractCount = countChineseEquivalent(formData.abstract);
  const socialScienceCount = countChineseEquivalent(formData.socialScienceContent);

  // Success page
  if (submitted) {
    return (
      <div className="submit-page">
        <div className="submit-container">
          <div className="submit-success-card">
            <div className="success-icon">✓</div>
            <h1 className="success-title">{currentTexts.successTitle}</h1>
            <p className="success-message">{currentTexts.successMessage}</p>
            <button className="submit-btn primary" onClick={handleBack}>
              {currentTexts.backButton}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="submit-page">
      <div className="submit-page-layout">
        {/* Main Content Area */}
        <div className="submit-main-content">
          <div className="submit-container">
            {/* Header Section */}
            <div className="submit-header">
              <h1 className={`submit-title ${language === 'en' ? 'en' : ''}`}>{currentTexts.pageTitle}</h1>
              <p className="submit-description">{currentTexts.pageDescription}</p>
            </div>

            {/* Mobile Sidebar Content - shows on smaller screens */}
            <div className="sidebar-mobile-wrapper">
              <DeadlineCountdown 
                language={language} 
                onDeadlineStatusChange={handleDeadlineStatusChange}
                previewMode={previewMode}
                targetDate={countdownTarget}
              />
            </div>

            {/* Call for Papers Section */}
            <div className="cfp-section">
              <h2 className="cfp-title">{currentTexts.cfpTitle}</h2>
              <p className="cfp-theme">{currentTexts.cfpTheme}</p>
              <p className="cfp-description">{currentTexts.cfpDescription}</p>
              <a 
                href="/Boya 2026 Cfp.pdf" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="cfp-download-btn"
              >
                {currentTexts.cfpDownload}
              </a>
              <p className="cfp-contact">{currentTexts.contactInfo}</p>
            </div>

            {/* Submission Closed Message */}
            {!previewMode && !isSubmissionOpen && (
              <div className="submission-closed-section">
                <div className="closed-icon-large">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                </div>
                <h2 className="closed-title-large">{currentTexts.submissionClosedTitle}</h2>
                <p className="closed-message-large">{currentTexts.submissionClosedMessage}</p>
              </div>
            )}

            {/* Error Section */}
            {(previewMode || isSubmissionOpen) && errors.length > 0 && (
              <div className="submit-error-section">
                <h3 className="error-title">{currentTexts.errorTitle}</h3>
                <ul className="error-list">
                  {errors.map((error, index) => (
                    <li key={index} className="error-item">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Form - only show when submissions are open */}
            {(previewMode || isSubmissionOpen) && (
            <form className="submit-form" onSubmit={handleSubmit}>
          {/* Forum Selection */}
          <div className="form-card">
            <div className="form-group">
              <label className="form-label required">{currentTexts.forumLabel}</label>
              <select
                className="form-select"
                value={formData.forum}
                onChange={(e) => handleInputChange('forum', e.target.value)}
              >
                <option value="">{currentTexts.forumPlaceholder}</option>
                {currentTexts.forumOptions.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Title */}
          <div className="form-card">
            <div className="form-group">
              <label className="form-label required">{currentTexts.titleLabel}</label>
              <input
                type="text"
                className={`form-input ${titleInfo.count > titleInfo.max ? 'error' : ''}`}
                placeholder={currentTexts.titlePlaceholder}
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
              />
              <div className="form-hint-row">
                <span className="form-hint">{currentTexts.titleHint}</span>
                <span className={`form-counter ${titleInfo.count > titleInfo.max ? 'error' : ''}`}>
                  {currentTexts.titleRemaining}: {Math.max(0, titleInfo.max - titleInfo.count)} {titleInfo.isEnglish ? currentTexts.titleWords : currentTexts.titleChars}
                </span>
              </div>
            </div>
          </div>

          {/* Abstract */}
          <div className="form-card">
            <div className="form-group">
              <label className="form-label required">{currentTexts.abstractLabel}</label>
              <textarea
                className={`form-textarea ${abstractCount > 800 ? 'error' : ''}`}
                placeholder={currentTexts.abstractPlaceholder}
                value={formData.abstract}
                onChange={(e) => handleInputChange('abstract', e.target.value)}
                rows={8}
              />
              <div className="form-hint-row">
                <span className="form-hint">{currentTexts.abstractHint}</span>
                <span className={`form-counter ${abstractCount > 800 ? 'error' : ''}`}>
                  {currentTexts.abstractCount}: {abstractCount}/800 ({currentTexts.abstractUnit})
                </span>
              </div>
            </div>
          </div>

          {/* Social Science Supplement */}
          <div className="form-card">
            <div className="form-group">
              <label className="form-label">{currentTexts.socialScienceLabel}</label>
              <div className="form-checkbox-row">
                <input
                  type="checkbox"
                  id="socialScienceEnabled"
                  className="form-checkbox"
                  checked={formData.socialScienceEnabled}
                  onChange={(e) => handleInputChange('socialScienceEnabled', e.target.checked)}
                />
                <label htmlFor="socialScienceEnabled" className="checkbox-label">
                  {currentTexts.socialScienceCheckbox}
                </label>
              </div>
              {formData.socialScienceEnabled && (
                <>
                  <textarea
                    className={`form-textarea ${socialScienceCount > 1000 ? 'error' : ''}`}
                    placeholder={currentTexts.socialSciencePlaceholder}
                    value={formData.socialScienceContent}
                    onChange={(e) => handleInputChange('socialScienceContent', e.target.value)}
                    rows={6}
                  />
                  <div className="form-hint-row">
                    <span className="form-hint">{currentTexts.socialScienceHint}</span>
                    <span className={`form-counter ${socialScienceCount > 1000 ? 'error' : ''}`}>
                      {currentTexts.abstractCount}: {socialScienceCount}/1000 ({currentTexts.abstractUnit})
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Keywords */}
          <div className="form-card">
            <div className="form-group">
              <label className="form-label required">{currentTexts.keywordsLabel}</label>
              <div className="keywords-input-row">
                <input
                  type="text"
                  className="form-input keywords-input"
                  placeholder={currentTexts.keywordsPlaceholder}
                  value={formData.keywordInput}
                  onChange={(e) => handleInputChange('keywordInput', e.target.value)}
                  onKeyPress={handleKeywordKeyPress}
                  disabled={formData.keywords.length >= 5}
                />
                <button
                  type="button"
                  className="keywords-add-btn"
                  onClick={addKeyword}
                  disabled={formData.keywords.length >= 5 || !formData.keywordInput.trim()}
                >
                  {currentTexts.keywordsAdd}
                </button>
              </div>
              <div className="form-hint-row">
                <span className="form-hint">{currentTexts.keywordsHint}</span>
                <span className="form-counter">
                  {currentTexts.keywordsRemaining} {5 - formData.keywords.length} {currentTexts.keywordsUnit}
                </span>
              </div>
              {formData.keywords.length > 0 && (
                <div className="keywords-tags">
                  {formData.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="keyword-tag"
                      onClick={() => removeKeyword(index)}
                      title={currentTexts.keywordsClickToRemove}
                    >
                      {keyword}
                      <span className="keyword-remove">×</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Author Information */}
          <div className="form-card">
            <h3 className="form-section-title">{currentTexts.authorInfoTitle}</h3>
            
            <div className="form-row">
              <div className="form-group half">
                <label className="form-label required">{currentTexts.authorNameLabel}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={currentTexts.authorNamePlaceholder}
                  value={formData.authorName}
                  onChange={(e) => handleInputChange('authorName', e.target.value.slice(0, 40))}
                  maxLength={40}
                />
                <span className="form-hint">{currentTexts.authorNameNote}</span>
              </div>
              <div className="form-group half">
                <label className="form-label required">{currentTexts.authorAffiliationLabel}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={currentTexts.authorAffiliationPlaceholder}
                  value={formData.authorAffiliation}
                  onChange={(e) => handleInputChange('authorAffiliation', e.target.value.slice(0, 40))}
                  maxLength={40}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group half">
                <label className="form-label required">{currentTexts.authorSeniorityLabel}</label>
                <select
                  className="form-select"
                  value={formData.authorSeniority}
                  onChange={(e) => handleInputChange('authorSeniority', e.target.value)}
                >
                  <option value="">{currentTexts.authorSeniorityPlaceholder}</option>
                  {currentTexts.authorSeniorityOptions.map((option, index) => (
                    <option key={index} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="form-group half">
                <label className="form-label required">{currentTexts.authorEmailLabel}</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder={currentTexts.authorEmailPlaceholder}
                  value={formData.authorEmail}
                  onChange={(e) => handleInputChange('authorEmail', e.target.value.slice(0, 40))}
                  maxLength={40}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group half">
                <label className="form-label">{currentTexts.authorPhoneLabel}</label>
                <input
                  type="tel"
                  className="form-input"
                  placeholder={currentTexts.authorPhonePlaceholder}
                  value={formData.authorPhone}
                  onChange={(e) => handleInputChange('authorPhone', e.target.value.slice(0, 40))}
                  maxLength={40}
                />
              </div>
              <div className="form-group half">
                <label className="form-label">{currentTexts.authorWechatLabel}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={currentTexts.authorWechatPlaceholder}
                  value={formData.authorWechat}
                  onChange={(e) => handleInputChange('authorWechat', e.target.value.slice(0, 40))}
                  maxLength={40}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label required">{currentTexts.authorCountryLabel}</label>
              <select
                className="form-select"
                value={formData.authorCountry}
                onChange={(e) => {
                  handleInputChange('authorCountry', e.target.value);
                  if (e.target.value !== '其他' && e.target.value !== 'Other') {
                    handleInputChange('authorCountryOther', '');
                  }
                }}
              >
                <option value="">{currentTexts.authorCountryPlaceholder}</option>
                {countries.map((country, index) => (
                  <option key={index} value={country[language] || country.en}>
                    {country[language] || country.en}
                  </option>
                ))}
              </select>
              {(formData.authorCountry === '其他' || formData.authorCountry === 'Other') && (
                <input
                  type="text"
                  className="form-input country-other-input"
                  placeholder={currentTexts.authorCountryOtherPlaceholder}
                  value={formData.authorCountryOther}
                  onChange={(e) => handleInputChange('authorCountryOther', e.target.value)}
                />
              )}
            </div>

            {/* Personal Bio */}
            <div className="form-group">
              <label className="form-label">{currentTexts.authorBioLabel}</label>
              <textarea
                className={`form-textarea ${formData.authorBio && !validateBioLength(formData.authorBio).valid ? 'error' : ''}`}
                placeholder={currentTexts.authorBioPlaceholder}
                value={formData.authorBio}
                onChange={(e) => handleInputChange('authorBio', e.target.value)}
                rows={3}
              />
              <div className="form-hint-row">
                <span className="form-hint">{currentTexts.authorBioHint}</span>
                <span className={`form-counter ${!validateBioLength(formData.authorBio).valid ? 'error' : ''}`}>
                  {validateBioLength(formData.authorBio).isEnglish 
                    ? `${validateBioLength(formData.authorBio).wordCount}/100 ${language === 'zh' ? '词' : 'words'}`
                    : `${formData.authorBio.length}/150 ${language === 'zh' ? '字符' : 'chars'}`}
                </span>
              </div>
            </div>
          </div>

          {/* Originality Declaration */}
          <div className="form-card">
            <div className="form-group">
              <label className="form-label required">{currentTexts.originalityLabel}</label>
              <div className="originality-box">
                <p className="originality-text">{currentTexts.originalityText}</p>
              </div>
              <div className="form-checkbox-row originality-checkbox">
                <input
                  type="checkbox"
                  id="originalityAgreed"
                  className="form-checkbox"
                  checked={formData.originalityAgreed}
                  onChange={(e) => handleInputChange('originalityAgreed', e.target.checked)}
                />
                <label htmlFor="originalityAgreed" className="checkbox-label">
                  {language === 'zh' ? '我已阅读并同意以上声明' : 'I have read and agree to the above declaration'}
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button type="button" className="submit-btn secondary" onClick={handleReset} disabled={isSubmitting}>
              {currentTexts.resetButton}
            </button>
            <button type="submit" className="submit-btn primary" disabled={isSubmitting}>
              {isSubmitting ? (language === 'zh' ? '提交中...' : 'Submitting...') : currentTexts.submitButton}
            </button>
          </div>
        </form>
            )}
          </div>
        </div>

        {/* Sidebar with Countdowns - Desktop only */}
        <div className="submit-sidebar">
          <div className="sidebar-countdown-top">
            <DeadlineCountdown 
              language={language} 
              onDeadlineStatusChange={handleDeadlineStatusChange}
              previewMode={previewMode}
              targetDate={countdownTarget}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Submit;
